import React, { useEffect, useRef, useState } from 'react';
import {
  getAdminProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  reorderProducts,
  getAdminBoutiques,
  Product,
  ProductInput,
  Boutique,
} from '../../../api/portalApi';
import ImageCropUploader from '../ImageCropUploader';

interface Props {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

type ProductSectionKey = 'food' | 'artisanal';

const currencyOptions = ['USD', 'EUR', 'TND', 'CNY', 'CFA'] as const;

const PRODUCT_SECTIONS: Array<{ value: ProductSectionKey; label: string }> = [
  { value: 'food', label: '🌱 Organic Food' },
  { value: 'artisanal', label: '🥁 Artisanal & Instruments' },
];

const SECTION_FIELDS: Record<ProductSectionKey, {
  categories: string[];
  storyLabel: string;
  storyPlaceholder: string;
  variantsLabel: string;
  variantsHint: string;
  variantLabelPlaceholder: string;
  descriptionPlaceholder: string;
}> = {
  food: {
    categories: ['Superfood', 'Herbal tea', 'Spice', 'Grain', 'Snack', 'Oil', 'Other'],
    storyLabel: 'Origin / sourcing story',
    storyPlaceholder: "Where it grows, who farms it, the harvest, why it's special…",
    variantsLabel: 'Weight / size variants',
    variantsHint: '(optional — e.g. 250g, 500g, 1kg)',
    variantLabelPlaceholder: 'Label (e.g. 500g)',
    descriptionPlaceholder: 'Short description shown on the card…',
  },
  artisanal: {
    categories: ['Djembe', 'Percussion', 'Drum', 'String instrument', 'Mask', 'Sculpture', 'Decor', 'Textile', 'Other'],
    storyLabel: 'Craft story / the artisan',
    storyPlaceholder: 'Who handcrafts it, the workshop, the wood & skins, the technique, how long it takes…',
    variantsLabel: 'Sizes / variants',
    variantsHint: '(optional — e.g. 12", 13", Standard, Pro)',
    variantLabelPlaceholder: 'Label (e.g. 13" head)',
    descriptionPlaceholder: 'Short description shown on the card…',
  },
};

const emptyForm = {
  name: '',
  description: '',
  origin: '',
  originStory: '',
  price: '',
  currency: 'EUR',
  stock: '',
  section: 'food' as ProductSectionKey,
  category: '',
  coverImageUrl: '',
  active: true,
  boutiqueId: '',
  variants: [] as Array<{ label: string; price: string }>,
  images: [] as string[],
  priceMatrix: [] as Array<{ label: string; options: Array<{ label: string; price: string; currency: string }> }>,
  customOrderAvailable: false,
  customOrderNote: '',
  internationalShipping: false,
};

const AdminProductsSection: React.FC<Props> = ({ showToast }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [boutiques, setBoutiques] = useState<Boutique[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderList, setReorderList] = useState<Product[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [selectedBoutiqueId, setSelectedBoutiqueId] = useState<string | null>(null);
  const formRef = useRef<HTMLElement>(null);

  useEffect(() => {
    Promise.all([getAdminProducts(), getAdminBoutiques()])
      .then(([pr, br]) => { setProducts(pr.products); setBoutiques(br.boutiques); setLoaded(true); })
      .catch(() => { setLoaded(true); });
  }, []);

  const reset = () => { setForm(emptyForm); setEditingId(null); };

  const refreshProducts = async () => {
    const r = await getAdminProducts();
    setProducts(r.products);
  };

  const moveProduct = (from: number, to: number) => {
    setReorderList((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item!);
      return next;
    });
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await reorderProducts(reorderList.map((p, i) => ({ id: p._id, position: i })));
      setProducts((prev) => {
        const updated = new Map(reorderList.map((p, i) => [p._id, i]));
        return [...prev].sort((a, b) => (updated.get(a._id) ?? 999) - (updated.get(b._id) ?? 999));
      });
      setReorderMode(false);
      showToast('Product order saved!');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to save order', 'error');
    } finally { setSavingOrder(false); }
  };

  const payload = (): ProductInput => ({
    name: form.name.trim(),
    description: form.description.trim(),
    origin: form.origin.trim(),
    originStory: form.originStory.trim(),
    price: Number(form.price),
    currency: form.currency,
    variants: form.variants.filter((v) => v.label.trim()).map((v) => ({ label: v.label.trim(), price: Number(v.price) || 0 })),
    priceMatrix: form.priceMatrix
      .filter((r) => r.label.trim())
      .map((r) => ({
        label: r.label.trim(),
        options: r.options.filter((o) => o.label.trim()).map((o) => ({
          label: o.label.trim(),
          price: Number(o.price) || 0,
          currency: o.currency || 'USD',
        })),
      })),
    customOrderAvailable: form.customOrderAvailable,
    customOrderNote: form.customOrderNote.trim(),
    stock: Number(form.stock) || 0,
    coverImageUrl: form.coverImageUrl,
    images: form.images,
    section: form.section,
    category: form.category.trim(),
    active: form.active,
    boutiqueId: form.boutiqueId || undefined,
    internationalShipping: form.internationalShipping,
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await updateProduct(editingId, payload());
        showToast('Product updated!');
      } else {
        await createProduct(payload());
        showToast('Product created!');
      }
      reset();
      await refreshProducts();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to save product', 'error');
    } finally { setSaving(false); }
  };

  const startEdit = (p: Product) => {
    setEditingId(p._id);
    setForm({
      name: p.name,
      description: p.description ?? '',
      origin: (p as any).origin ?? '',
      originStory: p.originStory ?? '',
      price: p.price.toString(),
      currency: p.currency,
      stock: (p.stock ?? 0).toString(),
      section: p.section === 'artisanal' ? 'artisanal' : 'food',
      category: p.category ?? '',
      coverImageUrl: p.coverImageUrl ?? '',
      active: p.active !== false,
      boutiqueId: p.boutiqueId ?? '',
      variants: (p.variants ?? []).map((v) => ({ label: v.label, price: v.price.toString() })),
      images: p.images ?? [],
      priceMatrix: (p.priceMatrix ?? []).map((r) => ({
        label: r.label,
        options: r.options.map((o) => ({ label: o.label, price: o.price.toString(), currency: o.currency })),
      })),
      customOrderAvailable: p.customOrderAvailable ?? false,
      customOrderNote: p.customOrderNote ?? '',
      internationalShipping: p.internationalShipping ?? false,
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const startNewInBoutique = (boutiqueId: string) => {
    setForm({ ...emptyForm, boutiqueId });
    setEditingId(null);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const remove = async (id: string) => {
    try {
      await deleteProduct(id);
      if (editingId === id) reset();
      await refreshProducts();
      showToast('Product deleted');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to delete product', 'error');
    }
  };

  const addVariant = () => setForm((f) => ({ ...f, variants: [...f.variants, { label: '', price: '' }] }));
  const updateVariant = (i: number, key: 'label' | 'price', value: string) =>
    setForm((f) => ({ ...f, variants: f.variants.map((v, idx) => idx === i ? { ...v, [key]: value } : v) }));
  const removeVariant = (i: number) =>
    setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));
  const addImage = (url: string) =>
    setForm((f) => f.images.length >= 4 ? f : ({ ...f, images: [...f.images, url] }));
  const removeImage = (i: number) =>
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  // Derived — products scoped to selected boutique
  const displayedProducts = selectedBoutiqueId === null
    ? products
    : selectedBoutiqueId === 'unassigned'
    ? products.filter((p) => !p.boutiqueId)
    : products.filter((p) => p.boutiqueId === selectedBoutiqueId);

  const filtered = search
    ? displayedProducts.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.category ?? '').toLowerCase().includes(search.toLowerCase()))
    : displayedProducts;

  const selectedBoutique = selectedBoutiqueId && selectedBoutiqueId !== 'unassigned'
    ? boutiques.find((b) => b._id === selectedBoutiqueId) ?? null
    : null;

  const boutiqueName = selectedBoutiqueId === 'unassigned'
    ? 'Unassigned'
    : (selectedBoutique?.name ?? '');

  // ── Boutique overview panel ────────────────────────────────────────────────
  const renderBoutiqueOverview = () => (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Products</h2>
        <span className="portal-item-badge">{products.length} total</span>
      </div>
      <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: 16 }}>
        Click a boutique to view and manage its products.
      </p>

      {!loaded ? (
        <p style={{ color: '#64748b' }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {boutiques.map((b) => {
            const count = products.filter((p) => p.boutiqueId === b._id).length;
            return (
              <button
                key={b._id}
                type="button"
                onClick={() => { setSelectedBoutiqueId(b._id); setSearch(''); setReorderMode(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '11px 14px', borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(255,255,255,0.03)',
                  cursor: 'pointer', textAlign: 'left', width: '100%',
                  transition: 'background 0.12s, border-color 0.12s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
              >
                {b.logoUrl
                  ? <img src={b.logoUrl} alt="" style={{ width: 42, height: 42, borderRadius: 9, objectFit: 'cover', flexShrink: 0 }} />
                  : <span style={{ width: 42, height: 42, borderRadius: 9, background: '#1e2535', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>🏪</span>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#f1f5f9', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {b.name}
                    {!b.active && <span className="hidden-badge">Inactive</span>}
                  </div>
                  <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>
                    {b.location ? `${b.location}  ·  ` : ''}{count} product{count !== 1 ? 's' : ''}
                  </div>
                </div>
                <span style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1 }}>›</span>
              </button>
            );
          })}

          {/* Unassigned bucket */}
          {(() => {
            const count = products.filter((p) => !p.boutiqueId).length;
            if (count === 0) return null;
            return (
              <button
                type="button"
                onClick={() => { setSelectedBoutiqueId('unassigned'); setSearch(''); setReorderMode(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '11px 14px', borderRadius: 12,
                  border: '1px dashed rgba(255,255,255,0.07)',
                  background: 'rgba(255,255,255,0.02)',
                  cursor: 'pointer', textAlign: 'left', width: '100%',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
              >
                <span style={{ width: 42, height: 42, borderRadius: 9, background: '#1a1f2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>📦</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#94a3b8', fontSize: '0.9rem' }}>Unassigned</div>
                  <div style={{ fontSize: '0.74rem', color: '#475569', marginTop: 2 }}>{count} product{count !== 1 ? 's' : ''} without a boutique</div>
                </div>
                <span style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1 }}>›</span>
              </button>
            );
          })()}

          {boutiques.length === 0 && (
            <p className="relation-empty">No boutiques yet — create boutiques first in the Boutiques section.</p>
          )}
        </div>
      )}
    </>
  );

  // ── Single boutique product list ───────────────────────────────────────────
  const renderBoutiqueProducts = () => (
    <>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <button
          type="button"
          onClick={() => { setSelectedBoutiqueId(null); setReorderMode(false); setSearch(''); reset(); }}
          style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8, padding: '5px 11px', color: '#94a3b8', cursor: 'pointer',
            fontSize: '0.78rem', flexShrink: 0, lineHeight: 1.4,
          }}
        >
          ← All
        </button>
        {selectedBoutique?.logoUrl && (
          <img src={selectedBoutique.logoUrl} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
        )}
        <h2 style={{ margin: 0, fontSize: '0.98rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {boutiqueName}
          <span className="portal-item-badge" style={{ marginLeft: 6 }}>{displayedProducts.length}</span>
        </h2>
        {!reorderMode && displayedProducts.length > 1 && (
          <button
            type="button"
            className="portal-btn-edit"
            onClick={() => { setReorderList([...displayedProducts]); setReorderMode(true); }}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            ↕ Reorder
          </button>
        )}
      </div>

      {/* Add product shortcut */}
      {selectedBoutiqueId !== 'unassigned' && !reorderMode && (
        <button
          type="button"
          onClick={() => startNewInBoutique(selectedBoutiqueId!)}
          style={{
            display: 'block', width: '100%', marginBottom: 12,
            padding: '9px 0', borderRadius: 10,
            border: '1px dashed rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.02)',
            color: '#64748b', fontSize: '0.82rem', fontWeight: 600,
            cursor: 'pointer', textAlign: 'center',
            transition: 'background 0.12s, color 0.12s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#94a3b8'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.color = '#64748b'; }}
        >
          + Add product to {boutiqueName}
        </button>
      )}

      {reorderMode ? (
        <>
          <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: 12 }}>
            Use ▲/▼ to set the display order for {boutiqueName}.
          </p>
          <div className="portal-stack">
            {reorderList.map((p, i) => (
              <div className="portal-item" key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="mu-position-badge" style={{ flexShrink: 0 }}>{i + 1}</span>
                {p.coverImageUrl
                  ? <img src={p.coverImageUrl} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                  : <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>🌿</span>}
                <span style={{ flex: 1, color: '#f1f5f9', fontSize: '0.88rem', fontWeight: 600 }}>{p.name}</span>
                {!p.active && <span className="hidden-badge">Inactive</span>}
                <div className="mu-move-btns">
                  <button type="button" className="mu-move-btn" onClick={() => moveProduct(i, i - 1)} disabled={i === 0} title="Move up">▲</button>
                  <button type="button" className="mu-move-btn" onClick={() => moveProduct(i, i + 1)} disabled={i === reorderList.length - 1} title="Move down">▼</button>
                </div>
              </div>
            ))}
          </div>
          <div className="portal-form-actions" style={{ marginTop: 16 }}>
            <button type="button" onClick={() => void saveOrder()} disabled={savingOrder}>
              {savingOrder ? 'Saving…' : 'Save order'}
            </button>
            <button type="button" onClick={() => setReorderMode(false)}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          {displayedProducts.length > 5 && (
            <input
              className="admin-search"
              placeholder={`Search in ${boutiqueName}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ marginBottom: 12 }}
            />
          )}
          <div className="portal-stack">
            {displayedProducts.length === 0 ? (
              <p className="relation-empty">
                {selectedBoutiqueId === 'unassigned'
                  ? 'All products are assigned to a boutique.'
                  : `No products in ${boutiqueName} yet — use the form on the left to add one.`}
              </p>
            ) : filtered.length === 0 ? (
              <p className="relation-empty">No products match "{search}".</p>
            ) : filtered.map((p) => (
              <div className="portal-item" key={p._id}>
                <div className="portal-item-head">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {p.coverImageUrl
                      ? <img src={p.coverImageUrl} alt="" style={{ width: 38, height: 38, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                      : <span style={{ fontSize: '1.3rem' }}>🌿</span>}
                    {p.name}
                    {!p.active && <span className="hidden-badge">Inactive</span>}
                  </h3>
                  {confirmDelete === p._id ? (
                    <div className="portal-inline-confirm">
                      <span>Delete?</span>
                      <button type="button" className="portal-btn-delete" onClick={() => { void remove(p._id); setConfirmDelete(null); }}>Yes</button>
                      <button type="button" className="portal-btn-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div className="portal-item-actions">
                      <button type="button" className="portal-btn-edit" onClick={() => startEdit(p)}>Edit</button>
                      <button type="button" className="portal-btn-delete" onClick={() => setConfirmDelete(p._id)}>Delete</button>
                    </div>
                  )}
                </div>
                {p.description && <p className="portal-item-meta">{p.description}</p>}
                <p className="portal-item-meta">
                  {p.price.toLocaleString()} {p.currency}
                  <span className="portal-item-badge">{p.section === 'artisanal' ? '🥁 Artisanal' : '🌱 Food'}</span>
                  {p.category && <span className="portal-item-badge">{p.category}</span>}
                  <span className="portal-item-badge">Stock {p.stock ?? 0}</span>
                  {p.variants?.length > 0 && <span className="portal-item-badge">{p.variants.length} variants</span>}
                  {p.internationalShipping
                    ? <span className="portal-item-badge" style={{ background: 'rgba(192,98,42,0.15)', color: '#c0622a' }}>🌍 International</span>
                    : <span className="portal-item-badge" style={{ background: 'rgba(100,116,139,0.1)', color: '#64748b' }}>📍 Local only</span>
                  }
                  {(p.waitlistCount ?? 0) > 0 && (
                    <span className="portal-item-badge" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>📬 {p.waitlistCount} waitlist</span>
                  )}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="admin-section-grid">
      {/* ── Form panel ─────────────────────────────────────────────────────── */}
      <article className="portal-card" ref={formRef}>
        <h2>{editingId ? 'Edit Product' : selectedBoutique ? `New Product — ${selectedBoutique.name}` : 'New Product'}</h2>
        <form className="portal-form" onSubmit={submit}>
          <label>Product section <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 400 }}>(which shop family this belongs to)</span></label>
          <select
            value={form.section}
            onChange={(e) => setForm({ ...form, section: e.target.value as ProductSectionKey, category: '' })}
          >
            {PRODUCT_SECTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <label>Product name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />

          <label>Origin <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 400 }}>(shown on card — e.g. "Yunnan Province · Handcrafted")</span></label>
          <input value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} placeholder="Province, region or craft descriptor…" />

          <label>Description</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={SECTION_FIELDS[form.section].descriptionPlaceholder} />

          <label>{SECTION_FIELDS[form.section].storyLabel}</label>
          <textarea value={form.originStory} onChange={(e) => setForm({ ...form, originStory: e.target.value })} placeholder={SECTION_FIELDS[form.section].storyPlaceholder} />

          <ImageCropUploader
            value={form.coverImageUrl}
            onChange={(url) => setForm({ ...form, coverImageUrl: url })}
            aspect={4 / 3}
            label="Cover image"
          />

          <label>Additional images <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 400 }}>(up to 4)</span></label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {form.images.map((url, i) => (
              <div key={url + i} style={{ position: 'relative', width: 72, height: 72, borderRadius: 8, overflow: 'hidden', background: '#0a0c14' }}>
                <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <button type="button" onClick={() => removeImage(i)} style={{ position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
              </div>
            ))}
            {form.images.length < 4 && (
              <div style={{ width: 180 }}>
                <ImageCropUploader value="" onChange={addImage} aspect={1} label="image" previewHeight={72} />
              </div>
            )}
          </div>

          <label>Price</label>
          <div className="portal-amount-row">
            <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
            <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              {currencyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <label>{SECTION_FIELDS[form.section].variantsLabel} <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 400 }}>{SECTION_FIELDS[form.section].variantsHint}</span></label>
          <div className="portal-multiselect" style={{ gap: 8 }}>
            {form.variants.length === 0 && (
              <p className="relation-empty" style={{ margin: 0 }}>No variants — the base price is used.</p>
            )}
            {form.variants.map((v, i) => (
              <div key={i} className="portal-amount-row" style={{ gridTemplateColumns: '1fr 1fr auto', display: 'grid', gap: 8, alignItems: 'center' }}>
                <input placeholder={SECTION_FIELDS[form.section].variantLabelPlaceholder} value={v.label} onChange={(e) => updateVariant(i, 'label', e.target.value)} />
                <input type="number" min="0" step="0.01" placeholder="Price" value={v.price} onChange={(e) => updateVariant(i, 'price', e.target.value)} />
                <button type="button" className="portal-btn-delete" onClick={() => removeVariant(i)} style={{ whiteSpace: 'nowrap' }}>✕</button>
              </div>
            ))}
            <button type="button" className="portal-btn-edit" onClick={addVariant} style={{ width: 'fit-content' }}>+ Add variant</button>
          </div>

          <label>Stock quantity</label>
          <input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="0" />

          <label>Category tag</label>
          <input
            list="product-category-list"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            placeholder={`e.g. ${SECTION_FIELDS[form.section].categories.slice(0, 3).join(', ')}…`}
            autoComplete="off"
          />
          <datalist id="product-category-list">
            {SECTION_FIELDS[form.section].categories.map((c) => <option key={c} value={c} />)}
          </datalist>

          <label>Boutique <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 400 }}>(pins this product to a boutique)</span></label>
          <select value={form.boutiqueId} onChange={(e) => setForm({ ...form, boutiqueId: e.target.value })}>
            <option value="">— No boutique —</option>
            {boutiques.filter((b) => b.active !== false).map((b) => (
              <option key={b._id} value={b._id}>{b.name}{b.location ? ` (${b.location})` : ''}</option>
            ))}
          </select>

          <label className="portal-hidden-toggle">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <span>Active — visible in the shop</span>
          </label>
          <label className="portal-hidden-toggle">
            <input
              type="checkbox"
              checked={form.internationalShipping}
              onChange={(e) => setForm({ ...form, internationalShipping: e.target.checked })}
            />
            <span>Available internationally</span>
          </label>
          <p style={{ fontSize: '0.76rem', color: '#64748b', margin: '-8px 0 4px', paddingLeft: 26, fontStyle: 'italic' }}>
            {form.internationalShipping
              ? 'Product has certifications required for international export'
              : "Only available in Côte d'Ivoire"}
          </p>

          <div className="portal-form-actions">
            <button type="submit" disabled={saving}>{saving ? 'Saving...' : editingId ? 'Update Product' : 'Create Product'}</button>
            {editingId && <button type="button" onClick={reset}>Cancel</button>}
          </div>
        </form>
      </article>

      {/* ── List panel ─────────────────────────────────────────────────────── */}
      <article className="portal-card">
        {selectedBoutiqueId === null ? renderBoutiqueOverview() : renderBoutiqueProducts()}
      </article>
    </div>
  );
};

export default AdminProductsSection;
